import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  add,
  compareDesc,
  differenceInDays,
  endOfMonth,
  format,
  setDate,
  startOfMonth,
  sub,
  isAfter,
  isBefore,
} from "date-fns";
import Cell from "./Cell";
import { ReactComponent as IcCalendar } from "../../asset/icon/ic_calendar.svg";
import { CalendarProps } from "../../interface/calendar";
import { useRecoilState } from "recoil";
import { calendarState } from "../../atoms";
import clsx from "clsx";

/**
 *
 * @param field react-hook-form 데이터 전송 변수
 * @param inputLen input 길이 설정
 * @param isStart 시작 날짜 설정 달력인지 아닌지 구분
 * @returns {JSX.Element} input + 달력
 * @constructor
 */
const Calendar = ({ field, inputLen, isStart = false }: CalendarProps) => {
  const [open, setOpen] = useState(false); // 달력 출력 여부를 관리
  const calendarRef: React.RefObject<HTMLElement | null> = useRef(null); // 달력 컴포넌트의 DOM 요소를 참조하는 변수
  const inputRef: React.RefObject<HTMLElement | null> = useRef(null); // input 컴포넌트의 DOM 요소를 참조하는 변수
  const [calendar, setCalendar] = useRecoilState(calendarState);

  // 시작 날짜의 최소/최대 제한
  const MIN_DATE = new Date(2022, 4, 1); // 2022년 5월 1일 (인덱스가 0부터 시작하므로 5월은 4)
  const TODAY = new Date(); // 오늘 날짜

  const resDate = calendar.minDate; // store 에서 선택 최소 일자를 가져온다
  const minDate = useMemo(() => {
    if (isStart) {
      // 시작 날짜인 경우, 2022년 5월 1일과 resDate 중 더 나중 날짜로 설정
      return resDate ? new Date(Math.max(new Date(resDate).getTime(), MIN_DATE.getTime())) : MIN_DATE;
    } else {
      // 종료 날짜인 경우, 선택된 시작 날짜를 최소 날짜로 설정
      return resDate ? new Date(resDate) : null;
    }
  }, [resDate, isStart]);

  // console.log("calendar", calendar)

  const [, setIsPicked] = useState(false);
  const selectedEndDateString = calendar.endDate;
  const selectedStartDateString = calendar.startDate;
  const selectedStartDate = selectedStartDateString
    ? new Date(selectedStartDateString)
    : null; // selectedStartDate 존재하면 Date로 변환, 존재하지 않으면 null로 설정

  const maxDate = useMemo(() => {
    if (isStart) {
      // 시작 날짜는 오늘 날짜를 초과할 수 없음
      return TODAY;
    } else if (selectedStartDate) {
      // 종료 날짜는 시작 날짜로부터 최대 7일까지 가능
      return add(selectedStartDate, { days: 7 });
    }
    return null;
  }, [isStart, selectedStartDate]);

  const [value, setValue] = useState(new Date()); //  날짜 값을 관리하며 초기값은 selectedStartDate 또는 오늘 날짜로 설정

  useEffect(() => {
    // 초기 minDate 설정을 2022년 5월 1일로
    setCalendar((prev) => ({
      ...prev,
      minDate: String(MIN_DATE),
    }));

    // 초기 value값은 시작 날짜인 경우 오늘 날짜와 2022년 5월 1일 중 더 나중 날짜로 설정
    if (isStart) {
      setValue(isBefore(TODAY, MIN_DATE) ? MIN_DATE : TODAY);
    } else {
      setValue(new Date());
    }

    // 모달에 있는 요소인지 아닌지 확인 후 모달 요소면 document 가 아닌 상위 요소에 이벤트 등록
    const parent = document.querySelector(`[id^='headlessui-dialog-panel-']`);
    if (parent) {
      // 모달에 있으면
      // 달력 외 영역을 선택했을 때 달력이 닫히게 하기 위해 상위 요소에 클릭 이벤트 등록
      parent.addEventListener("click", handleClickOutside as EventListener);
      return () => {
        // cleanup 함수로 이전에 등록한 이벤트 제거
        parent.removeEventListener(
          "click",
          handleClickOutside as EventListener,
        );
      };
    }
    // 달력 외 영역을 선택했을 때 달력이 닫히게 하기 위해 화면 전체에 클릭 이벤트 등록
    document.addEventListener("click", handleClickOutside);
    return () => {
      // cleanup 함수로 이전에 등록한 이벤트 제거
      document.removeEventListener("click", handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * value 상태 값을 업데이트하는 함수
   * @param date Date 객체
   *
   */
  const onChange = (date: Date) => setValue(date);
  useEffect(() => {
    // resDate 가 변경 될 때마다 value값을 변경 (종료 날짜)
    if (isStart) {
      field.onChange(String(new Date()));
    } else if (
      resDate &&
      compareDesc(new Date(resDate), new Date(selectedEndDateString)) < 0
    ) {


      if (minDate !== null) {
        setValue(new Date(minDate));
        setCalendar((prev) => ({
          ...prev,
          endDate: resDate,
        }));
      }
      field.onChange(String(resDate));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resDate]);

  // 시작일이 변경될 때마다 종료일을 자동으로 시작일 + 7일로 설정
  useEffect(() => {
    // 이미 처리 중인지 확인하기 위한 플래그
    let isProcessing = false;

    if (isProcessing) return;

    if (isStart && selectedStartDateString) {
      // 시작 날짜 컴포넌트에서는 종료 날짜를 설정만 함
      const startDate = new Date(selectedStartDateString);
      const endDate = add(startDate, { days: 7 });
      const formattedEndDate = format(endDate, 'yyyy-MM-dd');

      // 종료일이 이미 시작일+7일이면 업데이트 안 함
      if (selectedEndDateString === formattedEndDate) {
        return;
      }

      isProcessing = true;
      // console.log("formattedEndDate", formattedEndDate)
      setCalendar((prev) => ({
        ...prev,
        endDate: formattedEndDate
      }));

      // 다음 렌더 사이클에서 플래그 초기화
      setTimeout(() => {
        isProcessing = false;
      }, 0);
    } else if (!isStart && selectedStartDateString) {
      // 종료 날짜 컴포넌트에서는 시작일이 변경되면 종료일의 value도 함께 업데이트
      const startDate = new Date(selectedStartDateString);
      const newEndDate = add(startDate, { days: 7 });
      const formattedEndDate = format(newEndDate, 'yyyy-MM-dd');

      // 이미 적절한 값으로 설정되어 있으면 중복 업데이트 방지
      if (selectedEndDateString === formattedEndDate) {
        return;
      }

      // 현재 선택된 종료일이 시작일+7일보다 미래면 시작일+7일로 조정
      if (!selectedEndDateString || isAfter(newEndDate, new Date(selectedEndDateString))) {
        isProcessing = true;
        setValue(newEndDate);
        setCalendar((prev) => ({
          ...prev,
          endDate: formattedEndDate
        }));
        field.onChange(String(newEndDate));

        // 다음 렌더 사이클에서 플래그 초기화
        setTimeout(() => {
          isProcessing = false;
        }, 0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStart, selectedStartDateString]);



  useEffect(() => {
    if (isStart) {
      // 시작 날짜면
      setCalendar((prev) => ({ ...prev, startDate: String(value) }));
      setCalendar((prev) => ({ ...prev, minDate: String(value) }));
    } else {
      // 종료 날짜 선택 시 기간 제한 확인
      if (selectedStartDate) {
        const daysDiff = differenceInDays(value, selectedStartDate);

        // 7일을 초과하는 경우 시작일로부터 7일 후로 설정
        if (daysDiff > 7) {
          const maxAllowedDate = add(selectedStartDate, { days: 7 });
          setValue(maxAllowedDate);
          setCalendar((prev) => ({ ...prev, endDate: String(maxAllowedDate) }));
          field.onChange(String(maxAllowedDate));
          return;
        }
      }

      setCalendar((prev) => ({ ...prev, endDate: String(value) }));
    }
    field.onChange(String(value)); // 날짜 값 변경 후 use-form 에 전달
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);



  // 요일 배열
  const weeks = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  // 해당 달의 시작하는 날
  const startDate = startOfMonth(value);
  // 해당 달의 끝나는 날
  const endDate = endOfMonth(value);
  // 해당 달의 총 날 수
  const totalDays = differenceInDays(endDate, startDate) + 1;

  // 해당 달 시작 전 빈 칸 수
  const prefixDays = startDate.getDay();
  // 해당 달 종료 후 빈 칸 수
  const suffixDays = 6 - endDate.getDay();

  // const prevMonth = () => {
  //   // 2022년 5월 이전으로는 이동 불가
  //   const prevMonthDate = sub(value, { months: 1 });
  //   if (isStart && isBefore(prevMonthDate, MIN_DATE)) {
  //     // 2022년 5월 이전으로 가지 못하도록 함
  //     setValue(MIN_DATE);
  //     return;
  //   }

  //   if (
  //     // 시작 날짜거나, 선택된 시작 날짜가 없거나, 선택된 시작 날짜가 있고 최소 날짜 달 보다 클 경우
  //     isStart ||
  //     !selectedStartDate ||
  //     (selectedStartDate &&
  //       minDate &&
  //       (value.getFullYear() > minDate.getFullYear() ||
  //         (value.getFullYear() === minDate.getFullYear() &&
  //           value.getMonth() > minDate.getMonth())))
  //   ) {
  //     onChange(prevMonthDate);
  //     if (minDate && value.getDate() < minDate.getDate()) {
  //       setValue(minDate);
  //     }
  //   }
  // };

  const prevMonth = () => {
    const prevMonthDate = sub(value, { months: 1 });
    if (isBefore(prevMonthDate, MIN_DATE)) {
      setValue(MIN_DATE);
      return;
    }
    onChange(prevMonthDate);
  };


  const nextMonth = () => {
    // 시작 날짜인 경우 오늘 날짜를 초과하는 달로는 이동 불가
    if (isStart) {
      const nextMonthDate = add(value, { months: 1 });
      if (isAfter(nextMonthDate, TODAY)) {
        return;
      }
    }
    onChange(add(value, { months: 1 }));
  };

  // const prevYear = () => {
  //   // 2022년 5월 이전으로는 이동 불가
  //   const prevYearDate = sub(value, { years: 1 });
  //   if (isStart && prevYearDate.getFullYear() < 2022) {
  //     // 2022년 이전으로 가지 못하도록 함
  //     setValue(new Date(2022, 4, 1));
  //     return;
  //   }

  //   if (
  //     // 시작 날짜거나, 선택된 시작 날짜가 없거나, 선택된 시작 날짜가 있고 최소 날짜 년 보다 클 경우
  //     isStart ||
  //     !selectedStartDate ||
  //     (!!selectedStartDate &&
  //       minDate &&
  //       compareDesc(minDate, sub(value, { years: 1 })) >= 0)
  //   ) {
  //     onChange(prevYearDate);
  //   }
  // };

  const prevYear = () => {
    const prevYearDate = sub(value, { years: 1 });
    if (isBefore(prevYearDate, MIN_DATE)) {
      setValue(MIN_DATE);
      return;
    }
    onChange(prevYearDate);
  };

  const nextYear = () => {
    // 시작 날짜인 경우 오늘 날짜를 초과하는 년도로는 이동 불가
    if (isStart) {
      const nextYearDate = add(value, { years: 1 });
      if (isAfter(nextYearDate, TODAY)) {
        return;
      }
    }
    onChange(add(value, { years: 1 }));
  };

  /**
   * 달력 외의 영역을 클릭했을 때 달력을 닫는 함수
   * @param e 마우스 이벤트 객체
   */
  const handleClickOutside = (e: MouseEvent) => {
    const target = e.target as HTMLElement | null;
    if (
      // 달력 컴포넌트가 존재하고 달력 영역에 클릭 영역이 포함되어있지 않고 클릭 영역도 달력 영역이 아니면
      calendarRef.current &&
      !calendarRef.current.contains(target) &&
      e.target !== calendarRef.current
    ) {
      setOpen(false); // 달력 숨기기
    } else if (!calendarRef.current && inputRef?.current?.contains(target)) {
      // 달력 컴포넌트가 없고 클릭 영역이 input 영역이면
      setOpen(true); // 달력 보이기
    }
  };

  /**
   * 날짜 선택 함수
   * @param index 선택한 일자
   */
  const handleClickDate = (index: number) => {
    setIsPicked(true);
    const date = setDate(value, index); // 선택한 일자를 해당 달의 Date 형태로 변경하고 value 값으로 설정

    // 날짜 제한 조건 확인
    if (isStart) {
      // 시작 날짜인 경우 2022년 5월 이전이거나 오늘 날짜보다 미래이면 선택 불가
      if (isBefore(date, MIN_DATE) || isAfter(date, TODAY)) {
        return;
      }
    } else if (selectedStartDate) {
      // 종료 날짜인 경우 시작 날짜보다 이전이거나 시작 날짜+7일보다 나중이면 선택 불가
      if (isBefore(date, selectedStartDate) ||
        isAfter(date, add(selectedStartDate, { days: 7 }))) {
        return;
      }
    }

    onChange(date); // 날짜 상태값 업데이트

    if (isStart) {
      // 시작 날짜면
      const endDate = add(date, { days: 7 }); // 종료일은 시작일 + 7일

      // 시작 날짜 설정
      setCalendar((prev) => ({
        ...prev,
        startDate: String(date),
        minDate: String(date),
        // 종료 날짜도 함께 설정
        endDate: format(endDate, 'yyyy-MM-dd')
      }));
    } else {
      setCalendar((prev) => ({ ...prev, endDate: String(date) }));
    }

    field.onChange(String(date)); // 날짜 값 변경 후 use-form 에 전달
    setOpen(false); // 날짜 선택 후 달력 닫기
  };

  return (
    <div className={` w-[200px] h-[50px] ${inputLen}`}>
      <div className="absolute">
        <div
          ref={inputRef as React.RefObject<HTMLDivElement>}
          className={clsx(
            inputLen,
            "flex justify-between items-center h-[50px] border border-silver rounded-xl pl-[20px] pr-[20px] w-[200px]",
          )}
        >
          <input
            className={clsx(
              "bg-transparent h-full w-11/12 cursor-pointer focus:outline-0 ",
            )}
            value={format(value, "yyyy/MM/dd")}
            readOnly
          />
          <IcCalendar className="icon cursor-pointer " />
        </div>
        {open && (
          <div
            ref={calendarRef as React.RefObject<HTMLDivElement>}
            className="z-50 bg-white relative -left-6  w-[250px] border border-1 rounded-lg shadow-lg"
          >
            <div className="grid grid-cols-7 items-center justify-center text-center p-1">
              <Cell onClick={prevYear}>{"<<"}</Cell>
              <Cell onClick={prevMonth}>{"<"}</Cell>
              <Cell className=" col-span-3 font-bold">
                {format(value, "LLL yyyy")}
              </Cell>
              <Cell onClick={nextMonth}>{">"}</Cell>
              <Cell onClick={nextYear}>{">>"}</Cell>
              {weeks.map((week) => (
                <Cell className="text-xs font-bold uppercase" key={week}>
                  {week}
                </Cell>
              ))}
              {Array.from({ length: prefixDays }).map((_, index) => (
                <Cell key={index} />
              ))}
              {Array.from({ length: totalDays }).map((_, index) => {
                const date = index + 1;
                const formattedDate = setDate(value, date);
                const isCurrentDate = date === value.getDate();

                // 날짜 선택 불가능 조건 계산
                let isDisabled = false;

                if (isStart) {
                  // 시작 날짜인 경우: 2022년 5월 이전이거나 오늘 날짜 이후는 disabled
                  isDisabled = isBefore(formattedDate, MIN_DATE) || isAfter(formattedDate, TODAY);
                } else {
                  // 종료 날짜인 경우: 시작 날짜 이전이거나 시작 날짜+7일 이후는 disabled
                  if (minDate) {
                    isDisabled = isBefore(formattedDate, minDate);
                  }
                  if (selectedStartDate && !isDisabled) {
                    isDisabled = isAfter(formattedDate, add(selectedStartDate, { days: 7 }));
                  }
                }

                return (
                  <Cell
                    key={date}
                    isActive={isCurrentDate}
                    onClick={() => handleClickDate(date)}
                    disabled={isDisabled}
                  >
                    {date}
                  </Cell>
                );
              })}
              {Array.from({ length: suffixDays }).map((_, index) => (
                <Cell key={index} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Calendar;

